"use client";

import React from "react";
import { Shield, Crown, Gem, Medal, Award, Star, CircleDot } from "lucide-react";
import { REWARD_TIERS, getCurrentTier } from "../types/rewards.types";

const TIER_ICONS: Record<string, React.ElementType> = {
  Unranked: CircleDot,
  Bronze: Medal,
  Silver: Award,
  Gold: Star,
  Platinum: Shield,
  Diamond: Gem,
  Elite: Crown,
};

interface TierProgressBarProps {
  totalConversions: number;
}

export default function TierProgressBar({ totalConversions }: TierProgressBarProps) {
  const currentTier = getCurrentTier(totalConversions);
  // Skip "Unranked" in the visual display
  const displayTiers = REWARD_TIERS.filter((t) => t.name !== "Unranked");

  return (
    <div className="flex items-center w-full pb-2">
      {displayTiers.map((tier, index) => {
        const isActive = totalConversions >= tier.minConversions;
        const isCurrent = tier.name === currentTier.name;
        const Icon = TIER_ICONS[tier.name] || CircleDot;

        return (
          <React.Fragment key={tier.name}>
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                  isCurrent
                    ? `${tier.iconBg} ring-2 ring-offset-2 ${tier.borderColor}`
                    : isActive
                    ? tier.iconBg
                    : "bg-gray-100"
                }`}
              >
                <Icon
                  className={`h-5 w-5 ${isActive ? tier.color : "text-gray-300"}`}
                />
              </div>
              <span
                className={`text-xs font-medium ${
                  isActive ? tier.color : "text-gray-400"
                }`}
              >
                {tier.name}
              </span>
              <span className="text-[10px] text-gray-400">
                {tier.minConversions >= 1000
                  ? `${(tier.minConversions / 1000).toFixed(0)}k`
                  : tier.minConversions}
                +
              </span>
            </div>
            {index < displayTiers.length - 1 && (
              <div
                className={`flex-1 h-0.5 mt-[-20px] mx-1 ${
                  totalConversions >= displayTiers[index + 1].minConversions
                    ? "bg-indigo-400"
                    : "bg-gray-200"
                }`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
